/**
 * X402 Middleware
 * Framework-agnostic payment middleware with Coinbase compatibility
 */

import {
  PaymentRequirementsResponse,
  PaymentRequiredError,
  MiddlewareOptions,
  X402Error,
  FacilitatorError,

} from '../lib'
import { X402Client } from '../client/fetch'

// Framework-agnostic request/response interfaces
 interface FrameworkRequest {
  headers: Record<string, string | string[] | undefined>
  header?: (name: string) => string | undefined
  // Allow frameworks to add custom properties
  [key: string]: any
}

 interface FrameworkResponse {
  status: (code: number) => FrameworkResponse
  json: (data: any) => FrameworkResponse
  setHeader?: (name: string, value: string) => FrameworkResponse
  end?: (data?: string) => void
}

export type FrameworkNext = () => void | Promise<void>

// Settlement information added to request after successful payment
export interface X402Settlement {
  txHash: string
  networkId: string
  payer?: string
  gasUsed: any
}


// ============================================================================
// X402 Specification Compliance Utilities
// ============================================================================

/**
 * Decode X-PAYMENT header from base64 encoding or JSON format
 * Per X402 specification, header should be base64-encoded, but we support both formats for compatibility
 */
function decodeXPaymentHeader(headerValue: string): any {
  try {
    // Try to parse as JSON first (for backward compatibility)
    try {
      return JSON.parse(headerValue)
    } catch {
      // If JSON parsing fails, try base64 decode (per X402 specification)
      const jsonString = atob(headerValue)
      return JSON.parse(jsonString)
    }
  } catch (error) {
    throw new X402Error('Invalid X-PAYMENT header format', 'INVALID_HEADER', 400)
  }
}


/**
 * Validate payment payload
 */
function validatePaymentPayload(payload: any): void {
  if (!payload || typeof payload !== 'object') {
    throw new X402Error('Invalid payment payload', 'INVALID_PAYLOAD', 400)
  }

  if (!payload.payload || typeof payload.payload !== 'object') {
    throw new X402Error('Missing payment payload', 'MISSING_PAYLOAD', 400)
  }

  if (!payload.payload.signature || !payload.payload.transaction) {
    throw new X402Error('Missing signature or transaction', 'INVALID_PAYLOAD_STRUCTURE', 400)
  }
}

export class X402Middleware {
  private client: X402Client

  constructor(client: X402Client) {
    this.client = client
  }

  /**
   * Create payment middleware for an endpoint (legacy single-route support)
   */
  createMiddleware(options: MiddlewareOptions) {
    return async (req: FrameworkRequest, res: FrameworkResponse, next?: FrameworkNext) => {
      try {
        // Check if this is a payment request (has X-PAYMENT header)
        const xPaymentHeader = this.getXPaymentHeader(req)

        if (!xPaymentHeader) {
          // No payment header - return 402 with payment requirements
          const requirements = this.createPaymentRequirements(options)
          throw new PaymentRequiredError(requirements)
        }

        // Has payment header - verify and settle payment
        const settlement = await this.processPayment(xPaymentHeader, options)

        // Payment successful - add settlement info to request
        if (settlement && req) {
          req.x402Settlement = {
            txHash: settlement.txHash,
            networkId: settlement.networkId,
            payer: settlement.payer,
            gasUsed: settlement.gasUsed
          }
        }

        // Continue to handler
        next?.()

      } catch (error) {
        this.handleMiddlewareError(error, res, next)
      }
    }
  }


  /**
   * Process payment verification and settlement (legacy)
   */
  private async processPayment(xPaymentHeader: string, options: MiddlewareOptions) {
    try {
      // Decode and validate payment payload according to X402 specification
      const paymentPayload = decodeXPaymentHeader(xPaymentHeader)
      validatePaymentPayload(paymentPayload)

      // Create payment requirements for verification
      const paymentRequirementsResponse = this.createPaymentRequirements(options)

      // Extract single PaymentRequirement from accepts array (as expected by facilitator)
      const paymentRequirement = paymentRequirementsResponse.accepts[0]
      if (!paymentRequirement) {
        throw new X402Error('No payment requirement found', 'INVALID_REQUIREMENTS', 400)
      }

      // Step 1: Verify payment

      const verification = await this.client.verifyPayment(xPaymentHeader, paymentRequirement)

      if (!verification.isValid) {
        throw new X402Error(
          verification.invalidReason || 'Payment verification failed',
          'VERIFICATION_FAILED',
          402
        )
      }

      // Step 2: Settle payment

      const settlement = await this.client.settlePayment(xPaymentHeader, {
        network: paymentRequirement.network,
        amount: paymentRequirement.amount,
        asset: paymentRequirement.asset,
        payTo: paymentRequirement.payTo,
        description: paymentRequirement.description,
        extra: paymentRequirement.extra
      })

      if (!settlement.success) {
        throw new X402Error(
          settlement.error || 'Payment settlement failed',
          'SETTLEMENT_FAILED',
          402
        )
      }

      // Payment successful - add settlement info to request
      return settlement

    } catch (error) {
      if (error instanceof X402Error) throw error
      throw new X402Error('Payment processing failed', 'PROCESSING_ERROR', 500)
    }
  }

  /**
   * Create payment requirements object (legacy)
   */
  private createPaymentRequirements(options: MiddlewareOptions) {
    if (!options.payTo) {
      throw new X402Error('payTo address is required for payment processing', 'MISSING_PAYTO', 400)
    }

    const requirements: PaymentRequirementsResponse = {
      error: 'Payment required to access this resource',
      accepts: [{
        network: this.client['config'].network,
        amount: options.amount,
        asset: options.asset || 'SUI', // Default to SUI
        payTo: options.payTo,
        description: options.description || 'API Access Payment',
        extra: {
          symbol: options.asset || 'SUI'
        }
      }]
    }

    return requirements
  }

  /**
   * Extract X-PAYMENT header from request
   */
  private getXPaymentHeader(req: FrameworkRequest): string | null {
    // Hono/Fastify style (preferred)
    if (req.header && typeof req.header === 'function') {
      return req.header('x-payment') || req.header('X-PAYMENT') || null
    }

    // Express.js style
    const headerValue = req.headers?.['x-payment'] || req.headers?.['X-PAYMENT']
    if (headerValue) {
      return Array.isArray(headerValue) ? (headerValue[0] || null) : headerValue
    }

    return null
  }

  /**
   * Handle middleware errors with proper HTTP status codes
   */
  private handleMiddlewareError(error: any, res: FrameworkResponse, next?: FrameworkNext) {
    // Payment Required - return payment requirements
    if (error instanceof PaymentRequiredError) {
      this.sendResponse(res, 402, error.requirements)
      return
    }

    // X402 Protocol Errors - use the error's status code
    if (error instanceof X402Error) {
      const statusCode = error.statusCode || this.getStatusCodeForError(error.code)

      // For server errors (5xx), don't expose internal error details
      if (statusCode >= 500) {
        this.sendResponse(res, statusCode, {
          error: 'Internal server error',
          code: 'INTERNAL_ERROR'
        })
      } else {
        // For client errors (4xx), provide error details
        this.sendResponse(res, statusCode, {
          error: error.message,
          code: error.code
        })
      }
      return
    }

    // Facilitator Service Errors - treat as bad gateway
    if (error instanceof FacilitatorError) {
      this.sendResponse(res, 502, {
        error: 'Payment service unavailable',
        code: 'SERVICE_UNAVAILABLE'
      })
      return
    }

    // Network/Connection Errors
    if (error.code === 'ECONNREFUSED' || error.code === 'ENOTFOUND' || error.code === 'ETIMEDOUT') {
      this.sendResponse(res, 502, {
        error: 'Payment service unavailable',
        code: 'SERVICE_UNAVAILABLE'
      })
      return
    }

    // Timeout Errors
    if (error.name === 'AbortError' || error.message?.includes('timeout')) {
      this.sendResponse(res, 504, {
        error: 'Payment request timeout',
        code: 'REQUEST_TIMEOUT'
      })
      return
    }

    // Unknown errors - generic server error
    this.sendResponse(res, 500, {
      error: 'Internal server error',
      code: 'INTERNAL_ERROR'
    })
  }

  /**
   * Get appropriate HTTP status code for X402 error codes
   */
  private getStatusCodeForError(errorCode: string): number {
    switch (errorCode) {
      // Client errors (400-499)
      case 'INVALID_HEADER':
      case 'INVALID_PAYLOAD':
      case 'INVALID_PAYLOAD_STRUCTURE':
      case 'MISSING_PAYLOAD':
      case 'INVALID_REQUIREMENTS':
      case 'MISSING_PAYTO':
        return 400

      // Payment errors (402)
      case 'VERIFICATION_FAILED':
      case 'SETTLEMENT_FAILED':
      case 'PAYMENT_REQUIRED':
        return 402

      // Server errors (500-599)
      case 'PROCESSING_ERROR':
      case 'INTERNAL_ERROR':
      default:
        return 500
    }
  }

  /**
   * Send response with proper status and JSON data
   */
  private sendResponse(res: FrameworkResponse, statusCode: number, data: any, headers?: Record<string, string>) {
    // Set additional headers if provided
    if (headers) {
      for (const [name, value] of Object.entries(headers)) {
        if (res.setHeader) {
          res.setHeader(name, value)
        }
      }
    }

    // Express.js style
    if (typeof res.status === 'function' && typeof res.json === 'function' && !res.setHeader) {
      res.status(statusCode).json(data)
      return
    }

    // Hono style
    if (typeof res.status === 'function' && typeof res.json === 'function' && res.setHeader) {
      res.status(statusCode)
      res.json(data)
      return
    }
  }
}
